import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { t } from "ttag";

import { appQueriesStore } from "@/app/api";
import { usePortalUserStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { useScheduleContext } from "@/features/consultations/contexts";
import { useScheduleAppointment } from "@/features/consultations/hooks";
import type { Appointment } from "@/features/consultations/models";
import {
  ScheduleErrorModal,
  ScheduleSuccessModal,
} from "@/features/consultations/sections";
import { LegacyNavigationButtons } from "@/features/onboarding/components";
import { LogoLoader } from "@/shared/components";
import { useBoolean } from "@/shared/hooks";
import { ERROR_CODES } from "@/shared/models";
import { validateError } from "@/shared/ui";

export const ScheduleAppointmentSubmit = () => {
  const setNeedsInitialSync = usePortalUserStore(
    (state) => state.setNeedsEmbeddablesInitialSync,
  );

  const {
    language,
    scheduleDate: { timeSlot },
    appointmentType: type,
    subscriptionId,
    actions: { setProvider },
  } = useScheduleContext();

  const {
    value: showErrorModal,
    setFalse: closeErrorModal,
    setTrue: openErrorModal,
  } = useBoolean(false);
  const { value: showSuccessModal, setTrue: openSuccessModal } =
    useBoolean(false);

  const { mutation: scheduleAppointment } = useScheduleAppointment();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleSubmit = () => {
    if (timeSlot) {
      scheduleAppointment.mutate(
        { timeSlot, language, type, subscriptionId },
        {
          onError: (error: unknown) => {
            const validatedError = validateError(error);

            if (
              validatedError?.response.data.error.code ===
              ERROR_CODES.UNAVAILABLE_TIME_SLOT
            ) {
              return void queryClient.invalidateQueries({
                queryKey:
                  consultationsQueriesStore.consultations.providers._ctx
                    .availability.queryKey,
              });
            }

            openErrorModal();
          },
          onSuccess: (appointment: { provider: Appointment["provider"] }) => {
            if (appointment.provider) {
              setProvider(appointment.provider);
            }
            openSuccessModal();
          },
        },
      );
    }
  };

  const handleSuccessModalClose = () => {
    void queryClient.invalidateQueries({
      queryKey: appQueriesStore.user.queryKey,
    });
    setNeedsInitialSync(false);
  };

  return (
    <div className="flex grow items-end">
      <LegacyNavigationButtons
        continueLabel={t`Confirm consultation`}
        onContinue={handleSubmit}
        continueDisabled={!timeSlot || scheduleAppointment.isPending}
        onCancel={() => navigate(-1)}
      />

      <LogoLoader show={scheduleAppointment.isPending} fullscreen />

      <ScheduleErrorModal show={showErrorModal} onClose={closeErrorModal} />

      <ScheduleSuccessModal
        show={showSuccessModal}
        onClose={handleSuccessModalClose}
      />
    </div>
  );
};
